import React, { useEffect, useMemo, useState } from 'react'
import Login from "../pages/Login"
import FirstLogin from "../pages/FirstLogin"
import SetPassword from "../pages/SetPassword"
import { loadingState, loggedInState, userInfoState } from '../recoil/atoms'
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil'
import { getAccessToken } from '../api/authenticate'
import { isImpersonatedSession } from '../api/impersonation'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import EditProfile from './EditProfile'
import DepartmentDashboard from './department/Department'
import DepartmentManagerDashboard from './department/DepartmentManager'
import ContractorDashboard from './contractor/Contractor'
import { Backdrop, Box, CircularProgress, makeStyles } from '@material-ui/core'
import ContractorsList from './department/ContractorsList'
import Messages from './Messages'
import About from './About'
import AdminDashboard from './admin/Admin'
import VerifyDocumentTransfer from './VerifyDocumentTransfer'
import PaymentHistory from './admin/PaymentHistory'
import ContractorsPayments from './admin/ContractorsPayments'
import Billing from './admin/Billing'
import ContractorStats from './admin/ContractorStats'
import PremiumUsers from './admin/PremiumUsers'
import SiteSettingsPage from './admin/Settings'
import SupervisorDashboard from './supervisor/Supervisor'
import SupervisorDocumentsPage from "./supervisor/DocumentsPage"
import SupervisorRequestsList from './supervisor/RequestsList'
import SupervisorRequestDetail from './supervisor/RequestDetail'
import PaymentResult from '../pages/PaymentResult'
import DelayPage from './DelayPage'
import PrepaymentReport from './PrepaymentReport'
import PrepaymentPrint from './PrepaymentPrint'
import RoleSelect from './auth/RoleSelect'
import ConsultantDashboard from './consultant/ConsultantDashboard'
import UserManagement from './UserManagement'
import InvoicePublic from './InvoicePublic'
import SmsLogs from './SmsLogs'
import AuthReview from './admin/AuthReview'
import HelpPage from './HelpPage'
import GuidedTour from '../components/help/GuidedTour'
import TourLauncher from '../components/help/TourLauncher'
import LogoUploadPublic from './LogoUploadPublic'
import { TOUR_STEPS } from '../assets/helpContent'

// با ?debug=1 یک نوار وضعیت کوچک پایین صفحه نشان داده می‌شود (برای عیب‌یابیِ ورود/خروج)
const DEBUG_AUTH = typeof window !== 'undefined' && /[?&]debug=1/.test(window.location.search)

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}))

// کامپوننت داخلی که دسترسی به useNavigate داره
const RouterContent = ({ loggedIn, userInfo, setLoggedIn, setUserInfo }) => {
  const navigate = useNavigate()
  const loading = useRecoilValue(loadingState)

  // اگر کاربر دکمهٔ «رفرش» را زده باشد، بعد از بارگذاری به همان صفحه برگرد
  // (حتی اگر روتر به داشبورد بپرد) — «رفرش در همان صفحه».
  useEffect(() => {
    let to = null
    try { to = sessionStorage.getItem('afRefreshTo') } catch (e) { /* noop */ }
    if (!to) return
    try { sessionStorage.removeItem('afRefreshTo') } catch (e) { /* noop */ }
    if (to !== window.location.pathname + window.location.search) {
      navigate(to, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // DEBUG: بررسی userInfo
  useEffect(() => {
    console.log('🔍 UserInfo:', userInfo)
    console.log('🔍 role2:', userInfo?.role2)
  }, [userInfo])

  // activeRole: 'Contractor' | 'Consultant' | null
  const [activeRole, setActiveRole] = useState(() => {
    const saved = sessionStorage.getItem('activeRole')
    console.log('🔍 Saved activeRole from sessionStorage:', saved)
    return saved ? saved : null
  })

  const hasRole2 = useMemo(() => {
    const result = !!userInfo?.role2
    console.log('🔍 hasRole2:', result)
    return result
  }, [userInfo])

  // DEBUG: نمایش activeRole و hasRole2
  useEffect(() => {
    console.log('🔍 activeRole:', activeRole)
    console.log('🔍 hasRole2:', hasRole2)
    console.log('🔍 Should show role select?', hasRole2 && !activeRole)
  }, [activeRole, hasRole2])

  const handleSelectRole = (role) => {
    console.log('✅ Selected role:', role)
    setActiveRole(role)
    sessionStorage.setItem('activeRole', role)
    // بعد از انتخاب نقش، به صفحه اصلی برو
    navigate('/')
  }

  const clearActiveRole = () => {
    console.log('❌ Clearing role')
    setActiveRole(null)
    sessionStorage.removeItem('activeRole')
    // بعد از پاک کردن نقش، به صفحه انتخاب نقش برو
    navigate('/role')
  }

  const [adviceDismissed, setAdviceDismissed] = useState(() => {
    try { return localStorage.getItem('pwd_advice_dismissed') === '1' } catch (e) { return false }
  })

  // فقط وقتی «وارد شده» حساب می‌شود که نقشِ کاربر هم معلوم باشد؛ وگرنه روتر به حلقه می‌افتد و صفحه سفید می‌شود
  const authed = loggedIn === true && !!(userInfo && userInfo.role)

  // شمارهٔ همراه برای هر کاربر اجباری است ⇒ اگر ثبت نشده، تا وارد کردنِ آن
  // کاربر به صفحهٔ «ویرایش مشخصات» هدایت می‌شود.
  // استثنا: وقتی ادمین یا کاربرِ سازمان از پنلِ خودشان واردِ پنلِ پیمانکار شده‌اند،
  // اجبارِ گرفتنِ شمارهٔ تلفن از آن‌ها لازم نیست (فقط خودِ پیمانکار باید ثبت کند).
  const needsMobile = !!(
    userInfo &&
    userInfo.role &&
    userInfo.role !== 'Admin' &&
    !userInfo.mobile &&
    !isImpersonatedSession()
  )

  // تا وقتی وضعیت ورود (و نقشِ کاربر) مشخص نشده، هیچ صفحه‌ای نشان نده — لودر.
  // نکته: ReactDOM.render در حالت legacy این دو state را هم‌زمان batch نمی‌کند؛
  // لحظه‌ای loggedIn=true ولی نقش هنوز نرسیده است. آن لحظه نباید به /login بپرد
  // (وگرنه از /login با مسیر * روی / می‌افتد ⇒ «رفرشِ صفحه به داشبورد می‌رود»).
  if (loggedIn === null || (loggedIn === true && !authed)) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {authed && userInfo && userInfo.password_advice && !adviceDismissed && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3000,
          background: '#fffbeb', borderBottom: '1px solid #fcd34d', color: '#92400e',
          padding: '10px 16px', display: 'flex', gap: 12, alignItems: 'center',
          justifyContent: 'center', flexWrap: 'wrap', direction: 'rtl',
          fontSize: 13, fontWeight: 600,
        }}>
          <span>🔐 رمزِ شما با قواعدِ امنیتیِ جدید تعیین نشده است؛ برای امنیت و محرمانگیِ اطلاعاتِ خودتان پیشنهاد می‌شود آن را تغییر دهید.</span>
          <button
            type="button"
            onClick={() => window.location.assign('/set-password')}
            style={{ background: '#b45309', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}
          >
            تغییرِ رمز
          </button>
          <button
            type="button"
            onClick={() => {
              setAdviceDismissed(true)
              try { localStorage.setItem('pwd_advice_dismissed', '1') } catch (e) { /* noop */ }
            }}
            style={{ background: 'transparent', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            بعداً
          </button>
        </div>
      )}
      <Routes>
      {/* تعیین/تغییر رمز — هم برای کاربر واردشده و هم با توکنِ کوتاه‌عمر */}
      <Route path='/set-password' element={<SetPassword />} />
      {/* فاکتور عمومی — بدون نیاز به لاگین، در همه حالت‌ها قابل دسترسی */}
      <Route path='/invoice/:token' element={<InvoicePublic />} />
      {/* ثبتِ لوگو/عکس با موبایل (اسکنِ کیوآرکد) — بدون ورود */}
      <Route path='/logo-upload' element={<LogoUploadPublic />} />

      {authed ? (
        <>
          {userInfo?.first_time_login || needsMobile ? (
            <>
              <Route path='/edit' element={<EditProfile />} />
              <Route path='*' element={<Navigate to='/edit' />} />
            </>
          ) : (
            <>
              {/* اگر نقش دوم دارد و نقش فعال انتخاب نشده */}
              {hasRole2 && !activeRole && (
                <>
                  <Route
                    path='/role'
                    element={<RoleSelect onSelectRole={handleSelectRole} />}
                  />
                  <Route path='*' element={<Navigate to='/role' />} />
                </>
              )}

              {/* وقتی نقش دوم دارد و نقش فعال انتخاب شده */}
              {hasRole2 && activeRole && (
                <>
                  {activeRole === 'Consultant' && (
                    <>
                      <Route path='/' element={<ConsultantDashboard onSwitchRole={clearActiveRole} />} />
                      <Route path='/about' element={<About />} />
                      <Route path='/messages' element={<Messages />} />
                      <Route path='/contracts/:id/delay' element={<DelayPage />} />
                      <Route path='/contracts/:id/prepayment' element={<PrepaymentReport />} />
                      <Route path='/contracts/:id/prepayment/print' element={<PrepaymentPrint />} />
                      <Route path='/edit' element={<EditProfile />} />
                      <Route path='/user-management' element={<UserManagement />} />
                      <Route path='/role' element={<RoleSelect onSelectRole={handleSelectRole} />} />
                      <Route path='/help' element={<HelpPage />} />
                      <Route path='*' element={<Navigate to='/' />} />
                    </>
                  )}
                  {activeRole === 'Contractor' && (
                    <>
                      <Route path='/' element={<ContractorDashboard onSwitchRole={clearActiveRole} activeRole={activeRole} />} />
                      <Route path='/about' element={<About />} />
                      <Route path='/messages' element={<Messages />} />
                      <Route path='/contracts/:id/delay' element={<DelayPage />} />
                      <Route path='/contracts/:id/prepayment' element={<PrepaymentReport />} />
                      <Route path='/contracts/:id/prepayment/print' element={<PrepaymentPrint />} />
                      <Route path='/edit' element={<EditProfile />} />
                      <Route path='/user-management' element={<UserManagement />} />
                      <Route path='/role' element={<RoleSelect onSelectRole={handleSelectRole} />} />
                      <Route path='/help' element={<HelpPage />} />
                      <Route path='*' element={<Navigate to='/' />} />
                    </>
                  )}
                </>
              )}

              {/* اگر نقش دوم ندارد (activeRole اینجا بی‌اثر است — قبلاً از ورود جایگزین مانده بود) */}
              {!hasRole2 && (
                <>
                  {userInfo?.role === 'Department' && (
                    <>
                      <Route path='/' element={<DepartmentDashboard />} />
                      <Route path='/contractors' element={<ContractorsList />} />
                    </>
                  )}

                  {/* «مدیر سازمان» — مثلِ سازمان، ولی بدونِ آپلودِ اطلاعات */}
                  {userInfo?.role === 'DepartmentManager' && (
                    <>
                      <Route path='/' element={<DepartmentManagerDashboard />} />
                    </>
                  )}

                  {userInfo?.role === 'Supervisor' && (
                    <>
                      <Route path='/' element={<SupervisorDashboard />} />
                      <Route
                        path='/supervisor/contracts/:contractId/requests'
                        element={<SupervisorRequestsList />}
                      />
                      <Route
                        path='/supervisor/contracts/:contractId/requests/:requestId'
                        element={<SupervisorRequestDetail />}
                      />
                      <Route
                        path='/supervisor/contracts/:contractId/documents'
                        element={<SupervisorDocumentsPage />}
                      />
                    </>
                  )}

                  {userInfo?.role === 'Contractor' && (
                    <Route path='/' element={<ContractorDashboard />} />
                  )}

                  {userInfo?.role === 'Admin' && (
                    <>
                      <Route path='/' element={<AdminDashboard />} />
                      <Route path='/payments' element={<PaymentHistory />} />
                      <Route path='/billing' element={<Billing />} />
                      <Route path='/contractors-payments' element={<ContractorsPayments />} />
                      <Route path='/contractor-stats' element={<ContractorStats />} />
                      <Route path='/premium-users' element={<PremiumUsers />} />
                      <Route path='/settings' element={<SiteSettingsPage />} />
                      <Route path='/organizations' element={<AdminDashboard />} />
                      <Route path='/users' element={<AdminDashboard />} />
                      <Route path='/sms-logs' element={<SmsLogs />} />
        <Route path='/auth-review' element={<AuthReview />} />
                      <Route path='/help' element={<HelpPage />} />
                    </>
                  )}

                  <Route path='/messages' element={<Messages />} />
                  <Route path='/contracts/:id/delay' element={<DelayPage />} />
                      <Route path='/contracts/:id/prepayment' element={<PrepaymentReport />} />
                      <Route path='/contracts/:id/prepayment/print' element={<PrepaymentPrint />} />
                  <Route path='/edit' element={<EditProfile />} />
                  <Route path='/user-management' element={<UserManagement />} />
                  <Route path='/about' element={<About />} />
                  <Route path='/payment/result' element={<PaymentResult />} />
                  <Route path='/help' element={<HelpPage />} />
                  <Route path='*' element={<Navigate to='/' />} />
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
        
          <Route path='/login' element={<Login />} />
        <Route path='/first-login' element={<FirstLogin />} />
        <Route path='/set-password' element={<SetPassword />} />
          <Route path='/verify/:qrcode' element={<VerifyDocumentTransfer />} />
          <Route path='*' element={<Navigate to='/login' />} />
        </>
      )}
    </Routes>
    )
  </>
  )
}

const AppRouter = () => {
  const loading = useRecoilValue(loadingState)
  const classes = useStyles()
  const [loggedIn, setLoggedIn] = useRecoilState(loggedInState)
  const [userInfo, setUserInfo] = useRecoilState(userInfoState)
  const setLoading = useSetRecoilState(loadingState)

  useEffect(() => {
    // تا وقتی نشست بازیابی نشده، صفحهٔ ورود نشان داده نشود (به‌جایش لودر)
    setLoading(true)
    getAccessToken(setLoggedIn, setUserInfo).finally(() => {
      setLoading(false)
      // ایمنی: اگر به هر دلیل وضعیت ورود مشخص نشد، صفحهٔ ورود نشان داده شود (نه صفحهٔ خالی)
      setLoggedIn((v) => (v === null ? false : v))
    })
  }, [setLoggedIn, setUserInfo, setLoading])

  return (
    <>
      <BrowserRouter>
        <RouterContent
          loggedIn={loggedIn}
          userInfo={userInfo}
          setLoggedIn={setLoggedIn}
          setUserInfo={setUserInfo}
        />
        <TourLauncher />
      </BrowserRouter>

      {DEBUG_AUTH && (
        <Box
          dir="ltr"
          style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2500,
            background: 'rgba(0,0,0,.82)', color: '#00ff88',
            font: '11px/1.6 monospace', padding: '4px 8px', whiteSpace: 'pre-wrap',
          }}
        >
          {`loggedIn=${String(loggedIn)} loading=${String(loading)} role=${(userInfo && userInfo.role) || '-'} role2=${(userInfo && userInfo.role2) || '-'} activeRole=${sessionStorage.getItem('activeRole') || '-'} backup=${!!localStorage.getItem('impersonate_backup')} access=${!!localStorage.getItem('access')} refresh=${!!localStorage.getItem('refresh')} path=${window.location.pathname}${window.location.search}`}
        </Box>
      )}

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  )
}

export default AppRouter